import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
  HORIZON_DATE_WINDOW,
  HORIZON_FILTER_KEYS,
  HORIZON_STAGE_BADGE,
  DEFAULT_GROUP_BY,
} from './horizonConstants';
import { getTaskStageDisplayLabel } from './tasklistHelpers';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Pure helpers for Horizon grid computation (see docs/horizon-plan.md §5).
 * No Vuex access — HorizonLanding orchestrates these into recompute levels A/B/C.
 */

/** Formats a date as YYYY-MM-DD for grid column keys. */
export function formatDateKey(date) {
  if (!date) return '';
  return dayjs(date).format('YYYY-MM-DD');
}

/** Picks anchor date: today if in-cycle, otherwise cycle end (horizon-plan §5.1). */
export function resolveDefaultAnchoredDate(today, cycleStart, cycleEnd) {
  const t = dayjs(today).startOf('day');
  const start = dayjs(cycleStart).startOf('day');
  const end = dayjs(cycleEnd).startOf('day');

  if (!start.isValid() || !end.isValid()) {
    return t.format('YYYY-MM-DD');
  }

  if (t.isSameOrAfter(start) && t.isSameOrBefore(end)) {
    return t.format('YYYY-MM-DD');
  }

  return end.format('YYYY-MM-DD');
}

/** Derives visible horizonStartDate/horizonEndDate from anchor (default T−3 / T+4). */
export function deriveDateWindow(anchoredDate, daysBefore = HORIZON_DATE_WINDOW.DAYS_BEFORE, daysAfter = HORIZON_DATE_WINDOW.DAYS_AFTER) {
  const anchor = dayjs(anchoredDate);
  return {
    horizonStartDate: anchor.subtract(daysBefore, 'day').format('YYYY-MM-DD'),
    horizonEndDate: anchor.add(daysAfter, 'day').format('YYYY-MM-DD'),
  };
}

/** Returns inclusive day count between two YYYY-MM-DD dates. */
export function getDateRangeDayCount(startDate, endDate) {
  return dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
}

/** True if dateKey falls within [startDate, endDate] inclusive. */
export function isDateInRange(dateKey, startDate, endDate) {
  const d = dayjs(dateKey);
  return d.isSameOrAfter(dayjs(startDate), 'day') && d.isSameOrBefore(dayjs(endDate), 'day');
}

/** True if dateKey is Saturday or Sunday. */
export function isWeekend(dateKey) {
  const day = dayjs(dateKey).day();
  return day === 0 || day === 6;
}

/** True if any grid row has tasks on the given date column. */
export function columnHasTasks(rows, dateKey) {
  return rows.some((row) => (row.cells?.[dateKey]?.taskCount || 0) > 0);
}

/**
 * Sat+Sun pairs with no tasks in any row collapse into one weekend-stub column.
 * Keeps header and body column counts in sync.
 */
export function buildHorizonDisplayColumns(dateColumns = [], rows = []) {
  const columns = [];
  let i = 0;

  while (i < dateColumns.length) {
    const dateKey = dateColumns[i];

    if (dayjs(dateKey).day() === 6) {
      const sunKey = dateColumns[i + 1];
      const sunFollows = sunKey && dayjs(sunKey).day() === 0;

      if (sunFollows && !columnHasTasks(rows, dateKey) && !columnHasTasks(rows, sunKey)) {
        columns.push({
          type: 'weekend-stub',
          key: `weekend-${dateKey}`,
          dateKeys: [dateKey, sunKey],
        });
        i += 2;
        continue;
      }
    }

    columns.push({
      type: 'day',
      key: dateKey,
      dateKey,
    });
    i += 1;
  }

  return columns;
}

/** True if dateKey is within the close cycle boundaries. */
export function isDateInCycle(dateKey, cycleStart, cycleEnd) {
  if (!cycleStart || !cycleEnd) return false;
  const d = dayjs(dateKey);
  return d.isSameOrAfter(dayjs(cycleStart), 'day') && d.isSameOrBefore(dayjs(cycleEnd), 'day');
}

/** Builds ordered YYYY-MM-DD column keys for the visible date range. */
export function buildDateColumns(startDate, endDate) {
  const columns = [];
  let current = dayjs(startDate);
  const end = dayjs(endDate);
  while (current.isSameOrBefore(end, 'day')) {
    columns.push(current.format('YYYY-MM-DD'));
    current = current.add(1, 'day');
  }
  return columns;
}

/** Extracts YYYY-MM-DD due date key from a checklist task. */
export function getTaskDueDateKey(task) {
  if (!task?.duedate) return null;
  return formatDateKey(task.duedate);
}

/** Normalizes subcategory from task.subcategory or task.sub_category. */
export function getTaskSubcategory(task) {
  return task?.subcategory || task?.sub_category || '';
}

/** Builds in-app task detail path with cid and userType query params. */
export function buildTaskUrl({ checklistId, taskId, clientId, userType }) {
  return `/financialclose/project/checklist/${checklistId}/task/${taskId}?cid=${clientId}&userType=${userType}`;
}

/** Maps task stage number to display label (Pending Submission / Approval / Approved). */
export function getStatusBadge(stage) {
  return HORIZON_STAGE_BADGE[stage] || 'Unknown';
}

/** Percentage of tasks at stage 3 (Approved) in a cell or panel list. */
export function computeCompletionPercentage(tasks) {
  if (!tasks?.length) return 0;
  const approved = tasks.filter((t) => t.stage === 3).length;
  return Math.round((approved / tasks.length) * 100);
}

/** Resolves row group key from task fields based on active group-by dimension. */
function getGroupKey(task, groupBy) {
  switch (groupBy) {
    case 'location':
      return task.location || '__unassigned__';
    case 'user': {
      const userId = (task.preparer && task.preparer[0]) || (task.approver && task.approver[0]) || '__unassigned__';
      return userId;
    }
    case 'subcategory':
      return getTaskSubcategory(task) || '__unassigned__';
    case 'priority':
      return task.priority || '__unassigned__';
    default:
      return task.location || '__unassigned__';
  }
}

/** Resolves display label for a group key (maps ids to names where applicable). */
function getGroupLabel(groupKey, groupBy, userMap, locationMap) {
  if (groupKey === '__unassigned__') return 'Unassigned';

  switch (groupBy) {
    case 'location':
      return locationMap[groupKey]?.name || groupKey;
    case 'user':
      return userMap[groupKey]?.name || groupKey;
    case 'subcategory':
    case 'priority':
      return groupKey;
    default:
      return groupKey;
  }
}

/** True if userId appears in a preparer or approver assignee array. */
function userAssignedToTask(assignees, userId) {
  if (!userId || !Array.isArray(assignees)) return false;
  const normalizedUserId = String(userId);
  return assignees.some((id) => String(id) === normalizedUserId);
}

/** Filters checklist to tasks visible for manager/preparer/approver role. */
export function filterTasksByRole(tasks, userType, userId) {
  if (userType === 'manager') return tasks;
  if (userType === 'preparer') {
    return tasks.filter((task) => userAssignedToTask(task.preparer, userId));
  }
  if (userType === 'approver') {
    return tasks.filter((task) => userAssignedToTask(task.approver, userId));
  }
  return tasks;
}

/** Filters tasks whose due date falls within the horizon date window. */
export function filterTasksByDateRange(tasks, startDate, endDate) {
  return tasks.filter((task) => {
    const dateKey = getTaskDueDateKey(task);
    return dateKey && isDateInRange(dateKey, startDate, endDate);
  });
}

/** Filters tasks by status tab stage (null = all stages). */
export function filterTasksByTabStage(tasks, selectedTabStage) {
  if (selectedTabStage === null || selectedTabStage === undefined) return tasks;
  return tasks.filter((task) => task.stage === selectedTabStage);
}

/** Matches list view overdue filter: tasks with negative duedays. */
export function filterTasksByOverdue(tasks, overDueOnly = false) {
  if (!overDueOnly) return tasks;
  return (Array.isArray(tasks) ? tasks : []).filter(
    (task) => typeof task?.duedays === 'number' && task.duedays < 0
  );
}

/** Applies toolbar chip filters (status, priority, location, subcategory, user). */
export function filterTasksByChipFilters(tasks, activeFilters = {}) {
  let result = tasks;

  const statusFilter = activeFilters[HORIZON_FILTER_KEYS.STATUS];
  if (statusFilter?.length) {
    result = result.filter((task) => statusFilter.includes(task.stage));
  }

  const priorityFilter = activeFilters[HORIZON_FILTER_KEYS.PRIORITY];
  if (priorityFilter?.length) {
    result = result.filter((task) => priorityFilter.includes(task.priority));
  }

  const locationFilter = activeFilters[HORIZON_FILTER_KEYS.LOCATION];
  if (locationFilter?.length) {
    result = result.filter((task) => locationFilter.includes(task.location));
  }

  const subcategoryFilter = activeFilters[HORIZON_FILTER_KEYS.SUBCATEGORY];
  if (subcategoryFilter?.length) {
    result = result.filter((task) => subcategoryFilter.includes(getTaskSubcategory(task)));
  }

  const userFilter = activeFilters[HORIZON_FILTER_KEYS.USER];
  if (userFilter?.length) {
    result = result.filter((task) => {
      const preparers = task.preparer || [];
      const approvers = task.approver || [];
      return userFilter.some((uid) => preparers.includes(uid) || approvers.includes(uid));
    });
  }

  return result;
}

/** Level B scoping: role → date range → status tab → chip filters. */
export function getScopedTasks({
  checklist,
  userType,
  userId,
  horizonStartDate,
  horizonEndDate,
  selectedTabStage,
  activeHorizonFilters,
  overDueOnly = false,
}) {
  let tasks = Array.isArray(checklist) ? [...checklist] : [];
  tasks = filterTasksByRole(tasks, userType, userId);
  tasks = filterTasksByOverdue(tasks, overDueOnly);
  tasks = filterTasksByDateRange(tasks, horizonStartDate, horizonEndDate);
  tasks = filterTasksByTabStage(tasks, selectedTabStage);
  tasks = filterTasksByChipFilters(tasks, activeHorizonFilters);
  return tasks;
}

/** Level A: builds unique filter chip options from scoped tasks. */
export function buildFilterOptions(tasks, userMap, locationMap) {
  const statusSet = new Set();
  const prioritySet = new Set();
  const locationSet = new Set();
  const subcategorySet = new Set();
  const userSet = new Set();

  tasks.forEach((task) => {
    if (task.stage !== undefined && task.stage !== null) statusSet.add(task.stage);
    if (task.priority) prioritySet.add(task.priority);
    if (task.location) locationSet.add(task.location);
    const subcategory = getTaskSubcategory(task);
    if (subcategory) subcategorySet.add(subcategory);
    (task.preparer || []).forEach((id) => userSet.add(id));
    (task.approver || []).forEach((id) => userSet.add(id));
  });

  return {
    [HORIZON_FILTER_KEYS.STATUS]: [...statusSet]
      .sort((a, b) => a - b)
      .map((value) => ({ value, label: getStatusBadge(value) })),
    [HORIZON_FILTER_KEYS.PRIORITY]: [...prioritySet]
      .sort()
      .map((value) => ({ value, label: value })),
    [HORIZON_FILTER_KEYS.LOCATION]: [...locationSet]
      .sort((a, b) => (locationMap[a]?.name || '').localeCompare(locationMap[b]?.name || ''))
      .map((value) => ({ value, label: locationMap[value]?.name || value })),
    [HORIZON_FILTER_KEYS.SUBCATEGORY]: [...subcategorySet]
      .sort()
      .map((value) => ({ value, label: value })),
    [HORIZON_FILTER_KEYS.USER]: [...userSet]
      .sort((a, b) => (userMap[a]?.name || '').localeCompare(userMap[b]?.name || ''))
      .map((value) => ({ value, label: userMap[value]?.name || value })),
  };
}

/**
 * Adds panel-ready display fields (ref, status, overdue, taskUrl) to a raw task.
 * Server-side computed fields (e.g. locationName, tbtotals, threshold,
 * infoManagerRequestCounts) live on task.__computed when needed — read or pass
 * them through from there rather than re-deriving client-side.
 */
function enrichTaskDetail(task, { checklistId, clientId, userType, locationMap }) {
  const overdueDays = typeof task.duedays === 'number' ? Math.abs(task.duedays) : 0;
  const isOverdue = typeof task.duedays === 'number' && task.duedays < 0;

  return {
    _id: task._id,
    ref: task.ref,
    description: task.description,
    stage: task.stage,
    stageText: getTaskStageDisplayLabel(task) || getStatusBadge(task.stage),
    priority: task.priority,
    subcategory: getTaskSubcategory(task),
    locationId: task.location,
    locationName: locationMap[task.location]?.name || '',
    taskUrl: buildTaskUrl({
      checklistId,
      taskId: task._id,
      clientId,
      userType,
    }),
    isOverdue,
    overdueDays,
    statusBadge: getTaskStageDisplayLabel(task) || getStatusBadge(task.stage),
  };
}

/** Level B/C: groups scoped tasks into grid rows × date columns with panel task payloads. */
export function buildHorizonGridData({
  scopedTasks,
  groupBy = DEFAULT_GROUP_BY,
  startDate,
  endDate,
  checklistId,
  clientId,
  userType,
  userMap = {},
  locationMap = {},
}) {
  const dateColumns = buildDateColumns(startDate, endDate);
  const rowMap = new Map();

  scopedTasks.forEach((task) => {
    const groupKey = getGroupKey(task, groupBy);
    if (!rowMap.has(groupKey)) {
      rowMap.set(groupKey, {
        groupKey,
        groupLabel: getGroupLabel(groupKey, groupBy, userMap, locationMap),
        rowTotalCount: 0,
        subcategories: new Set(),
        cells: {},
      });
    }

    const row = rowMap.get(groupKey);
    const dateKey = getTaskDueDateKey(task);
    if (!dateKey) return;

    const rowSubcategory = getTaskSubcategory(task);
    if (rowSubcategory) {
      row.subcategories.add(rowSubcategory);
    }

    row.rowTotalCount += 1;

    if (!row.cells[dateKey]) {
      row.cells[dateKey] = {
        dueDate: dateKey,
        taskCount: 0,
        completionPercentage: 0,
        tasks: [],
      };
    }

    const cell = row.cells[dateKey];
    cell.taskCount += 1;
    cell.tasks.push(
      enrichTaskDetail(task, { checklistId, clientId, userType, locationMap })
    );
    cell.completionPercentage = computeCompletionPercentage(cell.tasks);
  });

  const rows = [...rowMap.values()]
    .map((row) => ({
      ...row,
      subcategories: [...row.subcategories].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));

  rows.forEach((row) => {
    dateColumns.forEach((dateKey) => {
      if (!row.cells[dateKey]) {
        row.cells[dateKey] = {
          dueDate: dateKey,
          taskCount: 0,
          completionPercentage: 0,
          tasks: [],
        };
      }
    });
  });

  return { rows, dateColumns };
}

/** Returns tasks for the selected grid cell (single row or all rows on a date). */
export function getPanelTasks(selectedCell, horizonGridData) {
  if (!selectedCell || !horizonGridData?.rows) return [];

  const { groupKey, dateKey } = selectedCell;
  if (!dateKey) return [];

  if (groupKey) {
    const row = horizonGridData.rows.find((r) => r.groupKey === groupKey);
    return row?.cells[dateKey]?.tasks || [];
  }

  const aggregated = [];
  horizonGridData.rows.forEach((row) => {
    const cellTasks = row.cells[dateKey]?.tasks || [];
    aggregated.push(...cellTasks);
  });
  return aggregated;
}

/** Builds task panel header (group label, formatted date, completion %, count). */
export function getPanelHeader({
  selectedCell,
  horizonGridData,
  anchoredDate,
}) {
  const tasks = getPanelTasks(selectedCell, horizonGridData);
  const completionPercentage = computeCompletionPercentage(tasks);
  const dateKey = selectedCell?.dateKey || anchoredDate;

  let groupLabel = 'All';
  if (selectedCell?.groupKey) {
    const row = horizonGridData?.rows?.find((r) => r.groupKey === selectedCell.groupKey);
    groupLabel = row?.groupLabel || 'Row';
  }

  return {
    groupLabel,
    formattedDate: dayjs(dateKey).format('MMM, D'),
    completionPercentage,
    taskCount: tasks.length,
  };
}

/**
 * Clamps a persisted or computed horizon window to cycle bounds and MAX_DAYS (15).
 * Keeps the start date fixed and shortens the end when the span is too wide.
 */
export function clampHorizonDateRange(
  startDate,
  endDate,
  {
    minDays = HORIZON_DATE_WINDOW.MIN_DAYS,
    maxDays = HORIZON_DATE_WINDOW.MAX_DAYS,
    cycleStart,
    cycleEnd,
  } = {}
) {
  if (!startDate || !endDate) return null;

  let start = dayjs(startDate).startOf('day');
  let end = dayjs(endDate).startOf('day');
  if (!start.isValid() || !end.isValid()) return null;

  if (end.isBefore(start, 'day')) {
    [start, end] = [end, start];
  }

  if (cycleStart && cycleEnd) {
    const cycleStartDay = dayjs(cycleStart).startOf('day');
    const cycleEndDay = dayjs(cycleEnd).startOf('day');
    if (start.isBefore(cycleStartDay, 'day')) start = cycleStartDay;
    if (end.isAfter(cycleEndDay, 'day')) end = cycleEndDay;
    if (end.isBefore(start, 'day')) return null;
  }

  const span = end.diff(start, 'day') + 1;
  if (span > maxDays) {
    end = start.add(maxDays - 1, 'day');
  }

  const adjustedSpan = end.diff(start, 'day') + 1;
  if (adjustedSpan < minDays && cycleStart && cycleEnd) {
    const cycleEndDay = dayjs(cycleEnd).startOf('day');
    const expandedEnd = start.add(minDays - 1, 'day');
    if (expandedEnd.isSameOrBefore(cycleEndDay, 'day')) {
      end = expandedEnd;
    }
  }

  return {
    horizonStartDate: start.format('YYYY-MM-DD'),
    horizonEndDate: end.format('YYYY-MM-DD'),
  };
}

/** Validates toolbar date range against HORIZON_DATE_WINDOW min/max day limits. */
export function validateDateRange(startDate, endDate, minDays = HORIZON_DATE_WINDOW.MIN_DAYS, maxDays = HORIZON_DATE_WINDOW.MAX_DAYS) {
  const count = getDateRangeDayCount(startDate, endDate);
  if (count < minDays) {
    return { valid: false, error: `Date range must be at least ${minDays} days` };
  }
  if (count > maxDays) {
    return { valid: false, error: `Date range cannot exceed ${maxDays} days` };
  }
  return { valid: true };
}
